import { useRef, useState } from "react";
import { toast } from "sonner";
import { supabase } from "@/integrations/supabase/client";
import { Voice } from "@/types/voice/voice";

// Default sample text for generating previews
const DEFAULT_PREVIEW_TEXT = "Hello! This is a preview of my voice. I hope you like how I sound.";

export const useVoicePlayback = () => {
  const [playingVoiceId, setPlayingVoiceId] = useState<string | null>(null);
  const [isGeneratingPreview, setIsGeneratingPreview] = useState(false);
  const audioRef = useRef<HTMLAudioElement | null>(null);

  const generateAndPlayPreview = async (voice: Voice) => {
    try {
      setIsGeneratingPreview(true);
      setPlayingVoiceId(voice.id);

      // Call the TTS edge function to generate audio
      const { data, error } = await supabase.functions.invoke("elevenlabs", {
        body: {
          resource: "tts",
          action: "generate",
          params: {
            voiceId: voice.id,
            text: DEFAULT_PREVIEW_TEXT,
            asBase64: true,
          },
        },
      });

      if (error) {
        throw new Error(error.message || "Failed to generate preview");
      }

      if (!data?.audioBase64) {
        throw new Error("No audio data returned");
      }

      // Create audio from base64
      const audioSrc = `data:${data.mimeType || "audio/mpeg"};base64,${data.audioBase64}`;
      const audio = new Audio(audioSrc);
      audioRef.current = audio;

      audio.addEventListener("ended", () => {
        setPlayingVoiceId(null);
        setIsGeneratingPreview(false);
        audioRef.current = null;
      });

      audio.addEventListener("error", () => {
        setPlayingVoiceId(null);
        setIsGeneratingPreview(false);
        audioRef.current = null;
        toast.error("Failed to play generated preview");
      });

      await audio.play();

      // Track voice usage if callback provided
      if (options.onTrackUsage) {
        options.onTrackUsage(voice.id);
      }
    } catch (error) {
      console.error("Error generating voice preview:", error);
      setPlayingVoiceId(null);
      setIsGeneratingPreview(false);
      toast.error("Failed to generate voice preview");
    }
  };

  const playVoicePreview = async (voice: Voice) => {
    // If already playing this voice, stop it
    if (playingVoiceId === voice.id && audioRef.current) {
      audioRef.current.pause();
      audioRef.current = null;
      setPlayingVoiceId(null);
      setIsGeneratingPreview(false);
      return;
    }

    // Stop any currently playing audio
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current = null;
    }

    // If voice has a preview URL, use it directly
    if (voice.preview_url) {
      try {
        setPlayingVoiceId(voice.id);

const audio = new Audio(voice.preview_url);
        audioRef.current = audio;

        audio.addEventListener("ended", () => {
          setPlayingVoiceId(null);
          audioRef.current = null;
        });

        audio.addEventListener("error", () => {
          setPlayingVoiceId(null);
          audioRef.current = null;
          toast.error("Failed to play voice preview");
        });

        await audio.play();
      } catch (error) {
        console.error("Error playing voice:", error);
        setPlayingVoiceId(null);
        audioRef.current = null;
        toast.error("Failed to play voice preview");
      }
    } else {
      // Generate preview using TTS
      await generateAndPlayPreview(voice);
    }
  };

  const stopPlayback = () => {
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current = null;
    }
    setPlayingVoiceId(null);
    setIsGeneratingPreview(false);
  };

  return {
    playingVoiceId,
    isGeneratingPreview,
    playVoicePreview,
    stopPlayback,
  };
};
